import React, { useState, useEffect } from 'react';
import { useCRM } from '../context/CRMContext';
import { db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { graphAPI, guessMapping, mapFBLead } from '../utils/facebookSync';
import { CRED_CONFIG, getCredStatus } from '../utils/integrationsRegistry';
import CredentialIntegrationDetail from '../components/integrations/CredentialIntegrationDetail';
import EmailDnsVerify from '../components/integrations/EmailDnsVerify';
import IntegrationDetailShell, { DetailRow, EmptyState, ActivityRow } from '../components/integrations/IntegrationDetailShell';

// ─── Facebook JS SDK loader + helpers ─────────────────────────────────────────
// Platform-level App ID — loaded from Firestore platformConfig/main at runtime.
// Falls back to env var / hardcoded value if Firestore hasn't loaded yet.
const PLATFORM_FB_APP_ID_FALLBACK = process.env.REACT_APP_FB_APP_ID || '1728065151881937';
const FB_SCOPE = 'leads_retrieval,pages_read_engagement,pages_show_list,pages_manage_metadata,pages_manage_ads,ads_read,business_management';
const FB_REQUIRED = ['leads_retrieval', 'pages_manage_ads', 'pages_show_list'];

function loadFBSDK(appId) {
  return new Promise((resolve) => {
    const init = () => {
      window.FB.init({ appId, version: 'v19.0', xfbml: false, cookie: true, status: true });
      resolve(window.FB);
    };
    if (window.FB) { init(); return; }
    window.fbAsyncInit = init;
    if (!document.getElementById('fb-jssdk')) {
      const s = document.createElement('script');
      s.id = 'fb-jssdk';
      s.src = 'https://connect.facebook.net/en_US/sdk.js';
      s.async = true;
      document.head.appendChild(s);
    }
  });
}

// Returns a fresh access token silently if already authorised, or null if not.
function fbGetLoginStatus(FB) {
  return new Promise((resolve) => {
    FB.getLoginStatus((r) => {
      resolve(r.status === 'connected' ? r.authResponse.accessToken : null);
    }, true); // true = force a fresh check with Facebook's servers
  });
}

// Triggers the FB login dialog and returns the token.
function fbLogin(FB) {
  return new Promise((resolve, reject) => {
    FB.login((r) => {
      if (r.authResponse?.accessToken) resolve(r.authResponse.accessToken);
      else reject(new Error(r.status === 'not_authorized' ? 'App not authorized. Please approve all permissions.' : 'Login window was closed.'));
    }, { scope: FB_SCOPE, return_scopes: true, auth_type: 'rerequest' });
  });
}

// ─── CRM fields available for mapping ────────────────────────────────────────
const CRM_MAPPABLE_FIELDS = [
  { key: 'firstName',  label: 'First Name' },
  { key: 'lastName',   label: 'Last Name' },
  { key: 'phone',      label: 'Phone / Mobile' },
  { key: 'email',      label: 'Email' },
  { key: 'city',       label: 'City' },
  { key: 'country',    label: 'Country' },
  { key: 'course',     label: 'Course / Program' },
  { key: 'budget',     label: 'Budget' },
  { key: 'source',     label: 'Source' },
  { key: 'priority',   label: 'Priority' },
  { key: '__skip__',   label: '— Skip this field —' },
];

// guessMapping() and mapFBLead() now live in ../utils/facebookSync.js so the
// background auto-sync effect in CRMContext.js can reuse the exact same logic.

// ─── Meta logo SVG ────────────────────────────────────────────────────────────
const MetaLogo = ({ size = 28 }) => (
  <svg viewBox="0 0 36 36" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 21.5C3 24.5 4.5 26.5 6.5 26.5C8.2 26.5 9.2 25.5 10.7 23.2L13.5 18.8L10.8 14.5C9.2 12.1 8.1 11.5 6.8 11.5C4.6 11.5 3 13.8 3 17.5V21.5Z" fill="#0081FB"/>
    <path d="M13.5 18.8L16.2 14.5C17.8 12.1 19.1 11.5 20.5 11.5C22.7 11.5 24 13.5 24 17C24 20.5 22.5 22.8 20.5 22.8C19.2 22.8 18.3 22 16.8 19.7L13.5 18.8Z" fill="#0064E0"/>
    <path d="M24 17C24 13.5 25.5 11.5 27.5 11.5C29.7 11.5 31 13.5 31 17C31 20.5 29.5 22.5 27.5 22.5C25.5 22.5 24 20.5 24 17Z" fill="#0081FB"/>
  </svg>
);

// ─── FB Field Mapping Dialog ──────────────────────────────────────────────────
function FBMappingDialog({ dialog, onConfirm, onCancel, importing, allSources }) {
  const [mapping, setMapping] = React.useState(dialog.mapping);
  const [selectedSource, setSelectedSource] = React.useState('facebook');
  const { form, page, rawLeads, allTotal, fbFields } = dialog;

  // Preview: first lead's field values
  const preview = rawLeads[0]?.field_data || [];
  const getPreviewVal = (fbKey) => (preview.find(f => f.name === fbKey)?.values || [])[0] || '—';

  return (
    <>
      {/* Backdrop */}
      <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)' }} />
      {/* Modal */}
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 2001,
        width: 'min(640px, 94vw)', maxHeight: '85vh', overflowY: 'auto',
        background: '#fff', borderRadius: 18, boxShadow: '0 24px 64px rgba(0,0,0,0.22)', padding: '28px 30px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Map Fields — {form.name}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
              {rawLeads.length} new lead{rawLeads.length !== 1 ? 's' : ''} from <strong>{page.name}</strong>
              {allTotal > rawLeads.length && <span> · {allTotal - rawLeads.length} already in CRM</span>}
            </div>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer', lineHeight: 1, padding: 0, flexShrink: 0 }}>✕</button>
        </div>

        {/* Ad analytics preview (auto-captured) */}
        {rawLeads[0]?.ad_name && (
          <div style={{ background: 'rgba(24,119,242,0.05)', border: '1px solid rgba(24,119,242,0.15)', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1877f2', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>📊 Ad Analytics — Auto-captured</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 11, color: '#475569' }}>
              {rawLeads[0].campaign_name && <div><span style={{ color: '#94a3b8' }}>Campaign: </span>{rawLeads[0].campaign_name}</div>}
              {rawLeads[0].adset_name    && <div><span style={{ color: '#94a3b8' }}>Ad Set: </span>{rawLeads[0].adset_name}</div>}
              {rawLeads[0].ad_name       && <div><span style={{ color: '#94a3b8' }}>Ad: </span>{rawLeads[0].ad_name}</div>}
              {rawLeads[0].platform      && <div><span style={{ color: '#94a3b8' }}>Platform: </span>{rawLeads[0].platform}</div>}
            </div>
          </div>
        )}

        {/* Source assignment */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
            Assign Source to These Leads
          </div>
          <select
            value={selectedSource}
            onChange={e => setSelectedSource(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 9, border: '1px solid #e2e8f0', fontSize: 13, color: '#0f172a', background: '#fff', cursor: 'pointer' }}>
            {(allSources || []).map(s => (
              <option key={s.value} value={s.value}>{s.icon} {s.label}</option>
            ))}
          </select>
        </div>

        {/* Field mapping table */}
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
          Map Facebook Fields → CRM Fields
        </div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 28px 1fr', gap: 0, background: '#f8fafc', padding: '8px 14px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Facebook Field</div>
            <div />
            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>CRM Field</div>
          </div>
          {fbFields.map((fbKey, i) => (
            <div key={fbKey} style={{ display: 'grid', gridTemplateColumns: '1fr 28px 1fr', alignItems: 'center', gap: 0,
              padding: '9px 14px', borderBottom: i < fbFields.length - 1 ? '1px solid #f1f5f9' : 'none',
              background: mapping[fbKey] === '__skip__' ? '#fafafa' : '#fff' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{fbKey}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>e.g. {getPreviewVal(fbKey)}</div>
              </div>
              <div style={{ fontSize: 16, color: '#cbd5e1', textAlign: 'center' }}>→</div>
              <select
                value={mapping[fbKey] || '__skip__'}
                onChange={e => setMapping(m => ({ ...m, [fbKey]: e.target.value }))}
                style={{ width: '100%', padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, color: '#0f172a', background: '#fff', cursor: 'pointer' }}>
                {CRM_MAPPABLE_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onCancel} disabled={importing}
            style={{ padding: '10px 20px', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={() => onConfirm(mapping, selectedSource)} disabled={importing}
            style={{ padding: '10px 22px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#1877f2,#3b82f6)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: importing ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            {importing
              ? <><span className="animate-spin" style={{ display: 'inline-block', width: 13, height: 13, borderRadius: '50%', border: '2px solid #fff', borderTopColor: 'transparent' }} /> Importing…</>
              : `Import ${rawLeads.length} Lead${rawLeads.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Connect screen — single click, no App ID needed ─────────────────────────
function ConnectScreen({ onConnect, onBack, loading, error }) {
  return (
    <div style={{ maxWidth: 440, margin: '40px auto' }}>
      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.09)', borderRadius: 20, padding: '32px 36px', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
          <div style={{ width: 52, height: 52, borderRadius: 13, background: 'rgba(24,119,242,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MetaLogo size={30} />
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Connect Facebook Pages</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>Sign in with your Facebook account</div>
          </div>
        </div>

        {[
          { icon: '✅', title: 'Auto-import leads', sub: 'New Lead Ad submissions flow straight into your pipeline' },
          { icon: '📋', title: 'All your lead gen forms', sub: 'See and sync every active form across all pages' },
          { icon: '🔒', title: 'Read-only access', sub: "We'll never post, run ads, or modify anything" },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
            <span style={{ fontSize: 16 }}>{item.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{item.title}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{item.sub}</div>
            </div>
          </div>
        ))}

        <div style={{ marginTop: 6, marginBottom: 18, padding: '10px 12px', background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.18)', borderRadius: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#d97706', marginBottom: 2 }}>💡 When Facebook asks</div>
          <div style={{ fontSize: 11, color: '#92400e', lineHeight: 1.5 }}>Tick <strong>all your pages</strong> and approve <strong>all permissions</strong> when prompted.</div>
        </div>

        {error && <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>{error}</div>}

        <button onClick={onConnect} disabled={loading}
          style={{ width: '100%', padding: '13px', borderRadius: 11, fontSize: 14, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', background: 'linear-gradient(135deg,#1877f2,#3b82f6)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, boxShadow: loading ? 'none' : '0 4px 14px rgba(24,119,242,0.35)' }}>
          {loading
            ? <><span className="animate-spin" style={{ display: 'inline-block', width: 15, height: 15, borderRadius: '50%', border: '2.5px solid #fff', borderTopColor: 'transparent' }} /> Connecting…</>
            : <><MetaLogo size={18} /> Continue with Facebook</>}
        </button>
        <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 12 }}>You can revoke access at any time from Facebook Settings.</p>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Integrations() {
  const { integrations: savedIntg, saveIntegrations, addLead, leads, customSources, companyId } = useCRM();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Merged sources list for mapping dialog
  const ALL_SOURCES_BASE = [
    { value: 'facebook',  label: 'Facebook'   },
    { value: 'instagram', label: 'Instagram'  },
    { value: 'whatsapp',  label: 'WhatsApp'   },
    { value: 'website',   label: 'Website'    },
    { value: 'google',    label: 'Google Ads' },
    { value: 'linkedin',  label: 'LinkedIn'   },
    { value: 'youtube',   label: 'YouTube'    },
    { value: 'email',     label: 'Email'      },
    { value: 'organic',   label: 'Organic'    },
    { value: 'referral',  label: 'Referral'   },
    { value: 'walkin',    label: 'Walk-in'    },
    { value: 'event',     label: 'Event'      },
    { value: 'agent',     label: 'Agent'      },
    { value: 'manual',    label: 'Manual'     },
  ];
  const mergedSources = React.useMemo(() => {
    const custom = (customSources || []).filter(cs => !ALL_SOURCES_BASE.find(s => s.value === cs.value));
    return [...ALL_SOURCES_BASE, ...custom];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customSources]);

  // ── Platform-level Facebook App ID (loaded from Firestore platformConfig/main)
  const [platformAppId, setPlatformAppId] = useState(PLATFORM_FB_APP_ID_FALLBACK);

  useEffect(() => {
    getDoc(doc(db, 'platformConfig', 'main'))
      .then(snap => {
        const appId = snap.exists() && snap.data().fbAppId;
        if (appId) setPlatformAppId(appId);
      })
      .catch(() => { /* use fallback */ });
  }, []);

  // ── view: 'overview' | 'facebook'
  const [view,          setView]          = useState('overview');
  // ── facebook flow step: 'loading' | 'connect' | 'connected'
  const [step,          setStep]          = useState('loading');
  const [fbToken,       setFbToken]       = useState('');
  const [pages,         setPages]         = useState([]);        // [{ id, name, access_token }]
  const [pageFormsMap,  setPageFormsMap]  = useState({});        // { pageId: { forms, loading, error } }
  const [syncing,       setSyncing]       = useState(null);      // { pageId, formId }
  const [syncResults,   setSyncResults]   = useState({});        // { formId: { imported, total } }
  const [loadingConn,   setLoadingConn]   = useState(false);
  const [error,         setError]         = useState('');
  const [tokenExpired,  setTokenExpired]  = useState(false);  // soft expiry — show banner, don't reset
  const [manualPageId,  setManualPageId]  = useState('');
  const [addingManual,  setAddingManual]  = useState(false);
  const [showAddPage,   setShowAddPage]   = useState(false);
  const [debugInfo,     setDebugInfo]     = useState(null);
  const [showDebug,     setShowDebug]     = useState(false);
  // ── Meta Ads detail tabs: 'overview' | 'connections' | 'resources' | 'activity'
  const [fbTab,         setFbTab]         = useState('connections');

  // ── FB field mapping dialog ───────────────────────────────────────────────
  const [mappingDialog, setMappingDialog] = useState(null); // { form, page, rawLeads, fbFields, mapping }
  const [mappingImporting, setMappingImporting] = useState(false);

  // ── credential forms state
  const [credView,    setCredView]    = useState(null);  // null | 'whatsapp' | 'gmail' | 'outlook' | 'twilio' | 'email' | 'googleAds' | 'aiCalling' | 'gsheets'
  const [credForm,    setCredForm]    = useState({});
  const [credSaving,  setCredSaving]  = useState(false);
  const [credSaved,   setCredSaved]   = useState(false);

  // ── personal-vs-work OAuth mode (gmail/outlook/zoom only — see oauth.js /modes)
  // { google: { mode, personalAvailable }, microsoft: {...}, zoom: {...} }
  const [oauthModes,    setOauthModes]    = useState({});
  const [oauthModesLoading, setOauthModesLoading] = useState(false);
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setOauthModesLoading(true);
    fetch(`${BACKEND_URL}/api/oauth/modes?companyId=${encodeURIComponent(companyId)}`)
      .then(r => r.json())
      .then(data => { if (!cancelled) setOauthModes(data || {}); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setOauthModesLoading(false); });
    return () => { cancelled = true; };
  }, [companyId]); // eslint-disable-line

  // ── Overview grid status filter — All / Connected / Pending / Available
  const [statusFilter, setStatusFilter] = useState('all');

  const saveCreds = async (key) => {
    setCredSaving(true); setCredSaved(false);
    await saveIntegrations({ [key]: { ...savedIntg?.[key], ...credForm, connected: true } });
    setCredSaving(false); setCredSaved(true);
    setTimeout(() => setCredSaved(false), 2500);
  };
  const disconnectCred = async (key) => {
    await saveIntegrations({ [key]: { connected: false } });
    setCredView(null);
  };
  // Opens any registry-driven credential card, pre-filling the form from
  // whatever was last saved for it (or blank for a first-time connect).
  const openCred = (key) => {
    const cfg = CRED_CONFIG[key];
    if (!cfg) return;
    const form = {};
    cfg.fields.forEach(f => { form[f.key] = savedIntg?.[key]?.[f.key] || ''; });
    if (cfg.supportsPersonalMode) {
      form.accountMode = savedIntg?.[key]?.accountMode === 'personal' ? 'personal' : 'work';
    }
    setCredForm(form);
    setCredView(key);
    setView('creds');
  };

  // ── Google Sheets import state
  const [gsheetsUrl,      setGsheetsUrl]      = useState('');
  const [gsheetsLoading,  setGsheetsLoading]  = useState(false);
  const [gsheetsError,    setGsheetsError]    = useState('');
  const [gsheetsPreview,  setGsheetsPreview]  = useState(null);   // { headers, rows }
  const [gsheetsMapping,  setGsheetsMapping]  = useState({});
  const [gsheetsImporting,setGsheetsImporting]= useState(false);
  const [gsheetsResult,   setGsheetsResult]   = useState(null);   // { imported }

  const CRM_FIELDS = ['firstName','lastName','email','phone','source','course','budget','country','status','priority','assignedTo'];

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return null;
    const parseRow = (line) => {
      const cols = []; let cur = ''; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') { inQ = !inQ; }
        else if (c === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
        else { cur += c; }
      }
      cols.push(cur.trim());
      return cols;
    };
    const headers = parseRow(lines[0]);
    const rows = lines.slice(1).map(l => parseRow(l));
    return { headers, rows };
  };

  const buildAutoMapping = (headers) => {
    const map = {};
    const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const aliases = {
      firstname: 'firstName', first: 'firstName', fname: 'firstName',
      lastname: 'lastName', last: 'lastName', lname: 'lastName',
      name: 'firstName', fullname: 'firstName',
      email: 'email', mail: 'email',
      phone: 'phone', mobile: 'phone', contact: 'phone', number: 'phone',
      source: 'source', channel: 'source', medium: 'source',
      course: 'course', program: 'course', programme: 'course',
      budget: 'budget', amount: 'budget',
      country: 'country', location: 'country', destination: 'country',
      stage: 'status', status: 'status',
      priority: 'priority',
      assignedto: 'assignedTo', assigned: 'assignedTo', counselor: 'assignedTo',
    };
    headers.forEach((h, i) => {
      const key = norm(h);
      if (aliases[key]) map[i] = aliases[key];
    });
    return map;
  };

  const fetchGsheet = async (url) => {
    setGsheetsLoading(true); setGsheetsError(''); setGsheetsPreview(null); setGsheetsResult(null);
    try {
      // Convert various Google Sheets URL formats to CSV export URL
      let csvUrl = url.trim();
      const match = csvUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
      if (match) {
        const id = match[1];
        // Use the export URL format
        csvUrl = `https://docs.google.com/spreadsheets/d/${id}/export?format=csv&usp=sharing`;
      }
      const res = await fetch(csvUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status} — make sure the sheet is publicly shared`);
      const text = await res.text();
      const parsed = parseCSV(text);
      if (!parsed) throw new Error('Could not parse CSV — sheet may be empty');
      setGsheetsPreview(parsed);
      setGsheetsMapping(buildAutoMapping(parsed.headers));
    } catch (e) {
      setGsheetsError(e.message || 'Failed to fetch sheet. Make sure it is publicly accessible.');
    } finally {
      setGsheetsLoading(false);
    }
  };

  const importGsheetsLeads = async () => {
    if (!gsheetsPreview) return;
    setGsheetsImporting(true);
    const { headers, rows } = gsheetsPreview;
    let count = 0;
    for (const row of rows) {
      const lead = { source: 'google_sheets', priority: 'warm' };
      Object.entries(gsheetsMapping).forEach(([colIdx, field]) => {
        const val = (row[parseInt(colIdx)] || '').trim();
        if (val) lead[field] = val;
      });
      if (!lead.firstName && !lead.phone && !lead.email) continue;
      // Split full name if only firstName captured
      if (lead.firstName && lead.firstName.includes(' ') && !lead.lastName) {
        const parts = lead.firstName.split(' ');
        lead.firstName = parts[0];
        lead.lastName = parts.slice(1).join(' ');
      }
      try { await addLead(lead); count++; } catch (_) {}
    }
    setGsheetsResult({ imported: count });
    setGsheetsImporting(false);
    await saveIntegrations({ gsheets: { connected: true, sheetUrl: gsheetsUrl, lastImport: new Date().toISOString(), lastCount: count } });
  };

  const exportLeadsCSV = () => {
    const headers = ['First Name','Last Name','Email','Phone','Source','Stage','Course','Country','Budget','Priority','Assigned To','Date Added'];
    const rows = leads.map(l => [
      l.firstName || '', l.lastName || l.name || '', l.email || '', l.phone || '',
      l.source || '', l.status || '', l.course || '', l.country || (l.countries?.[0] || ''),
      l.budget || '', l.priority || '', l.assignedTo || '',
      l.createdAt?.toDate ? l.createdAt.toDate().toLocaleDateString('en-IN') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'clarion_leads_' + new Date().toISOString().slice(0,10) + '.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const fbLeadsInCRM = leads.filter(l => l.source === 'facebook');
  const getImportedCount = (formId) => fbLeadsInCRM.filter(l => l.fbFormId === formId).length;

  // ── Load forms for a single page ─────────────────────────────────────────
  const loadPageForms = async (page) => {
    setPageFormsMap(prev => ({ ...prev, [page.id]: { ...prev[page.id], loading: true, error: null } }));
    try {
      const data = await graphAPI(`/${page.id}/leadgen_forms`, {
        access_token: page.access_token,
        fields: 'name,id,status,leads_count,created_time',
        limit: 100,
      });
      if (data.error) {
        const code = data.error.code;
        if (code === 190 || code === 102 || code === 463 || code === 467) {
          // Actual token expired — show soft banner, keep pages visible
          setTokenExpired(true);
          setPageFormsMap(prev => ({ ...prev, [page.id]: { forms: [], loading: false, error: 'token_expired' } }));
        } else if (code === 200 || code === 10 || code === 3) {
          // Permission error — pages_manage_ads missing
          setPageFormsMap(prev => ({ ...prev, [page.id]: { forms: [], loading: false, error: 'missing_permission' } }));
        } else {
          setPageFormsMap(prev => ({ ...prev, [page.id]: { forms: [], loading: false, error: data.error.message } }));
        }
      } else {
        setPageFormsMap(prev => ({ ...prev, [page.id]: { forms: data.data || [], loading: false, error: null } }));
      }
    } catch (e) {
      setPageFormsMap(prev => ({ ...prev, [page.id]: { forms: [], loading: false, error: 'Network error' } }));
    }
  };

  // ── Restore from Firestore on load ────────────────────────────────────────
  const hasRestored = React.useRef(false);
  useEffect(() => {
    if (hasRestored.current) return;
    if (savedIntg === undefined || savedIntg === null) return;
    hasRestored.current = true;

    const saved = savedIntg?.facebook;
    if (!saved?.connected) { setStep('connect'); return; }

    const savedPages = saved.pages || {};
    const restored = Object.entries(savedPages).map(([id, p]) => ({ id, name: p.name, access_token: p.accessToken }));
    if (restored.length > 0) {
      setPages(restored);
      setStep('connected');
      // Silently get a fresh token via the FB JS SDK — no popup, works while the
      // user is logged into Facebook in their browser. Fall back gracefully if not.
      loadFBSDK(platformAppId).then(FB => fbGetLoginStatus(FB)).then(freshToken => {
        if (freshToken) {
          setFbToken(freshToken);
          setTokenExpired(false);
          // Refresh page tokens and re-fetch forms with the new token
          const updatedPages = restored.map(p => ({ ...p, access_token: freshToken }));
          setPages(updatedPages); // ← critical: sync button uses page.access_token from pages state
          updatedPages.forEach(p => loadPageForms(p));
        } else {
          // Not logged in silently — forms will show token-expired msg, banner prompts reconnect
          setTokenExpired(true);
          restored.forEach(p =>
            setPageFormsMap(prev => ({ ...prev, [p.id]: { forms: [], loading: false, error: 'token_expired' } }))
          );
        }
      }).catch(() => {
        setTokenExpired(true);
      });
    } else {
      setStep('connect');
    }
  }, [savedIntg]); // eslint-disable-line

  // ── Collect all pages from personal profile + Business Manager ──────────────
  const fetchAllPages = async (token) => {
    let allPages = [];

    // 1. Personal profile pages (/me/accounts)
    try {
      const personal = await graphAPI('/me/accounts', { access_token: token, fields: 'name,id,access_token,fan_count,category', limit: 200 });
      if (!personal.error && personal.data?.length > 0) {
        allPages = [...allPages, ...personal.data];
      }
    } catch {}

    // 2. Business Manager pages — try /me/businesses then owned_pages + client_pages
    try {
      const bizData = await graphAPI('/me/businesses', { access_token: token, fields: 'id,name', limit: 50 });
      if (!bizData.error && bizData.data?.length > 0) {
        for (const biz of bizData.data) {
          // Owned pages
          try {
            const owned = await graphAPI(`/${biz.id}/owned_pages`, { access_token: token, fields: 'name,id,access_token,fan_count,category', limit: 200 });
            if (!owned.error && owned.data?.length > 0) {
              owned.data.forEach(p => { if (!allPages.find(x => x.id === p.id)) allPages.push(p); });
            }
          } catch {}
          // Client pages
          try {
            const client = await graphAPI(`/${biz.id}/client_pages`, { access_token: token, fields: 'name,id,access_token,fan_count,category', limit: 200 });
            if (!client.error && client.data?.length > 0) {
              client.data.forEach(p => { if (!allPages.find(x => x.id === p.id)) allPages.push(p); });
            }
          } catch {}
        }
      }
    } catch {}

    return allPages;
  };

  // ── Connect / Reconnect via FB JS SDK ────────────────────────────────────────
  const handleConnect = async () => {
    setError('');
    setTokenExpired(false);
    setLoadingConn(true);
    try {
      const FB = await loadFBSDK(platformAppId);

      // Try silent refresh first; if not authed, show the login dialog
      let token = await fbGetLoginStatus(FB);
      if (!token) token = await fbLogin(FB);

      setFbToken(token);

      const permData = await graphAPI('/me/permissions', { access_token: token });
      const granted = (permData.data || []).filter(p => p.status === 'granted').map(p => p.permission);
      setDebugInfo({ grantedPermissions: granted });

      // If any required permissions are missing, force re-login to request them
      const missingPerms = FB_REQUIRED.filter(p => !granted.includes(p));
      if (missingPerms.length > 0) {
        token = await fbLogin(FB); // shows FB dialog with auth_type: rerequest
      }

      const meData = await graphAPI('/me', { access_token: token, fields: 'id,name' });

      // Fetch pages from all sources (personal + Business Manager)
      const allPages = await fetchAllPages(token);

      if (allPages.length > 0) {
        const pagesMap = {};
        allPages.forEach(p => { pagesMap[p.id] = { name: p.name, accessToken: p.access_token, category: p.category, fanCount: p.fan_count }; });
        saveIntegrations({ facebook: { appId: platformAppId, connected: true, userId: meData.id, userName: meData.name, pages: pagesMap, connectedAt: new Date().toISOString() } });
        const pageObjs = allPages.map(p => ({ id: p.id, name: p.name, access_token: p.access_token, category: p.category, fan_count: p.fan_count }));
        setPages(pageObjs);
        setStep('connected');
        pageObjs.forEach(p => loadPageForms(p));
      } else {
        // Still nothing — save connected state and let user add manually
        saveIntegrations({ facebook: { appId: platformAppId, connected: true, userId: meData.id, userName: meData.name, pages: {}, connectedAt: new Date().toISOString() } });
        setStep('connected');
        setShowAddPage(true);
        const hasBiz = granted.includes('business_management');
        setError(hasBiz
          ? 'No pages found in your account or Business Manager. Make sure your Facebook page is published and you are an Admin of it, then add it by Page ID below.'
          : 'No pages found. If your page is managed via Meta Business Suite, reconnect and make sure to approve all requested permissions when prompted.'
        );
      }
    } catch (err) {
      setError(err.message || 'Connection failed. Please try again.');
    }
    setLoadingConn(false);
  };

  // ── Add page manually by ID ───────────────────────────────────────────────
  const handleManualPage = async () => {
    const token = fbToken || savedIntg?.facebook?.token;
    if (!manualPageId.trim() || !token) { setError('Connect to Facebook first.'); return; }
    setAddingManual(true);
    setError('');
    try {
      const data = await graphAPI(`/${manualPageId.trim()}`, { access_token: token, fields: 'name,id,access_token' });
      if (data.error || !data.id) { setError(data.error?.message || 'Page not found.'); setAddingManual(false); return; }
      const pageToken = data.access_token || token;
      const page = { id: data.id, name: data.name, access_token: pageToken };
      setPages(prev => prev.find(p => p.id === page.id) ? prev : [...prev, page]);
      setManualPageId('');
      setShowAddPage(false);
      // Persist to Firestore
      const existingPages = savedIntg?.facebook?.pages || {};
      saveIntegrations({ facebook: { ...savedIntg?.facebook, pages: { ...existingPages, [page.id]: { name: page.name, accessToken: pageToken } } } });
      loadPageForms(page);
    } catch (e) {
      setError('Network error. Please try again.');
    }
    setAddingManual(false);
  };

  // Shared import routine — used by both the first-time mapping dialog
  // confirm AND every silent re-sync afterwards, so the two paths can never
  // produce different results.
  const importLeadsForForm = async (form, page, rawLeads, allTotal, mapping, source) => {
    let imported = 0;
    let firstError = null;
    for (const fbLead of rawLeads) {
      try {
        const lead = mapFBLead(fbLead, page.id, page.name, form.id, form.name, mapping, null);
        lead.source = source || lead.source || 'facebook';
        await addLead(lead);
        imported++;
      } catch (err) {
        console.error('[FBSync] addLead failed for FB lead', fbLead.id, err?.message || err);
        if (!firstError) firstError = err?.message || String(err);
      }
    }
    if (firstError && imported === 0) {
      setError('Import failed: ' + firstError + '. Check browser console for details.');
    }
    setSyncResults(prev => ({ ...prev, [form.id]: { imported, total: allTotal, alreadyInCRM: allTotal - rawLeads.length, ts: Date.now() } }));
    return imported;
  };

  // Persist the field mapping the FIRST time a form is synced, keyed under
  // integrations.facebook.pages[pageId].forms[formId]. Firestore's
  // setDoc(..., {merge:true}) — which saveIntegrations() always uses —
  // recursively merges nested map fields, so this only touches this one
  // form's entry and leaves every other page/form/token untouched.
  // Once this exists, neither a manual "Sync" click nor the 15-minute
  // background auto-sync (CRMContext.js) will ever re-show the mapping
  // dialog for this form again.
  const persistFormMapping = (page, form, mapping, source) => {
    saveIntegrations({
      facebook: {
        pages: { [page.id]: { forms: { [form.id]: { mapping, source, name: form.name, mappedAt: new Date().toISOString() } } } },
        [`sync_${form.id}`]: new Date().toISOString(),
      },
    });
  };

  // ── Sync a form — silent if already mapped, otherwise show mapping dialog once ──
  const handleSyncForm = async (form, page) => {
    setSyncing({ pageId: page.id, formId: form.id });
    setError('');
    const existingFbIds = new Set(fbLeadsInCRM.map(l => l.fbLeadId));
    // Use the freshest available token: page token first, then the session-level user token
    const token = page.access_token || fbToken;
    if (!token) { setError('No access token available. Please reconnect your Facebook account.'); setSyncing(null); return; }
    try {
      const data = await graphAPI(`/${form.id}/leads`, {
        access_token: token,
        fields: 'field_data,created_time,id,form_id,ad_id,ad_name,adset_id,adset_name,campaign_id,campaign_name,platform',
        limit: 200,
      });
      if (data.error) {
        const msg = data.error.message || 'Facebook API error';
        console.error('[FBSync] API error:', data.error);
        setError(msg);
        // Also stamp the sync result so the inline label shows the error, not stale "Up to date"
        setSyncResults(prev => ({ ...prev, [form.id]: { imported: 0, total: 0, error: msg, ts: Date.now() } }));
        setSyncing(null);
        return;
      }
      const all = data.data || [];
      console.log(`[FBSync] form ${form.id}: ${all.length} total leads from FB`);
      const newLeads = all.filter(l => !existingFbIds.has(l.id));
      console.log(`[FBSync] form ${form.id}: ${newLeads.length} new (not yet in CRM)`);

      if (newLeads.length === 0) {
        setSyncResults(prev => ({ ...prev, [form.id]: { imported: 0, total: all.length, alreadyInCRM: all.length, ts: Date.now() } }));
        setSyncing(null);
        return;
      }

      // Already mapped from a previous sync (manual or auto) — import
      // straight away, no popup. This is the main fix for "I have to sync
      // again and again": previously every single click re-opened the
      // mapping dialog even for a form whose mapping never changes.
      const savedForm = savedIntg?.facebook?.pages?.[page.id]?.forms?.[form.id];
      if (savedForm?.mapping) {
        await importLeadsForForm(form, page, newLeads, all.length, savedForm.mapping, savedForm.source);
        saveIntegrations({ facebook: { [`sync_${form.id}`]: new Date().toISOString() } });
        setSyncing(null);
        return;
      }

      // First time syncing this form — collect fields, auto-guess a mapping,
      // and let the admin confirm/adjust it once.
      const fbFieldSet = new Set();
      newLeads.forEach(l => (l.field_data || []).forEach(f => fbFieldSet.add(f.name)));
      const fbFields = [...fbFieldSet];

      const autoMapping = {};
      fbFields.forEach(f => { autoMapping[f] = guessMapping(f); });

      setSyncing(null);
      setMappingDialog({ form, page, rawLeads: newLeads, allTotal: all.length, fbFields, mapping: autoMapping });
    } catch (e) {
      setError('Network error during sync.');
      setSyncing(null);
    }
  };

  // ── Confirm mapping (first sync only) — import, then persist the mapping ──
  const handleConfirmMapping = async (finalMapping, chosenSource) => {
    if (!mappingDialog) return;
    const { form, page, rawLeads, allTotal } = mappingDialog;
    setMappingImporting(true);
    await importLeadsForForm(form, page, rawLeads, allTotal, finalMapping, chosenSource);
    persistFormMapping(page, form, finalMapping, chosenSource);
    setMappingImporting(false);
    setMappingDialog(null);
  };

  // ── Disconnect ────────────────────────────────────────────────────────────
  const handleDisconnect = (pageId) => {
    if (pageId) {
      // Remove single page
      const updated = pages.filter(p => p.id !== pageId);
      setPages(updated);
      setPageFormsMap(prev => { const n = { ...prev }; delete n[pageId]; return n; });
      const existingPages = savedIntg?.facebook?.pages || {};
      const newPages = { ...existingPages };
      delete newPages[pageId];
      saveIntegrations({ facebook: { ...savedIntg?.facebook, pages: newPages } });
      if (updated.length === 0) setStep('connect');
    } else {
      // Full disconnect
      setPages([]);
      setPageFormsMap({});
      setFbToken('');
      setStep('connect');
      saveIntegrations({ facebook: { appId: platformAppId, connected: false, pages: {} } });
    }
  };

  // ── Retry forms with a pasted page access token ───────────────────────────
  const handleRetryWithPAT = (page, pat) => {
    if (!pat) return;
    const updated = { ...page, access_token: pat };
    setPages(prev => prev.map(p => p.id === page.id ? updated : p));
    const existingPages = savedIntg?.facebook?.pages || {};
    saveIntegrations({ facebook: { ...savedIntg?.facebook, pages: { ...existingPages, [page.id]: { name: page.name, accessToken: pat } } } });
    loadPageForms(updated);
  };

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <div className="page-enter" style={{ height: '100%', overflowY: 'auto', padding: '24px 28px' }}>

      {/* ── OVERVIEW ── */}
      {view === 'overview' && (
        <div>
          {/* Breadcrumb */}
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
            <span style={{ cursor: 'pointer', color: '#475569' }}>Integrations</span>
            <span style={{ margin: '0 6px' }}>›</span>
            <span>Integrations</span>
          </div>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>Connect your apps to bring all your data into one central place.</p>

          {/* Status filter chips */}
          {(() => {
            const metaStatus = (step === 'connected' && pages.length > 0) ? 'connected' : 'available';
            const gsheetsStatus = getCredStatus(savedIntg?.gsheets);
            const credStatuses = Object.keys(CRED_CONFIG).map(k => getCredStatus(savedIntg?.[k]));
            const allStatuses = [metaStatus, gsheetsStatus, ...credStatuses, 'available' /* zapier, coming soon */];
            const counts = { all: allStatuses.length, connected: 0, pending: 0, available: 0 };
            allStatuses.forEach(s => { if (counts[s] != null) counts[s]++; });
            const CHIPS = [
              { id: 'all',       label: 'All' },
              { id: 'connected', label: 'Connected' },
              { id: 'pending',   label: 'Pending' },
              { id: 'available', label: 'Available' },
            ];
            return (
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {CHIPS.map(chip => (
                  <div
                    key={chip.id}
                    onClick={() => setStatusFilter(chip.id)}
                    style={{
                      padding: '6px 14px', borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                      background: statusFilter === chip.id ? '#0f172a' : '#fff',
                      color: statusFilter === chip.id ? '#fff' : '#475569',
                      border: `1px solid ${statusFilter === chip.id ? '#0f172a' : 'rgba(0,0,0,0.1)'}`,
                    }}
                  >
                    {chip.label}{counts[chip.id] != null ? ` (${counts[chip.id]})` : ''}
                  </div>
                ))}
              </div>
            );
          })()}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {/* Meta Ads — bespoke, real OAuth flow */}
            {(statusFilter === 'all' || statusFilter === (step === 'connected' && pages.length > 0 ? 'connected' : 'available')) && (
              <OverviewCard
                logo={<MetaLogo size={28} />}
                name="Meta Ads"
                provider="Meta Platforms, Inc."
                description="Import Lead Ads submissions from Facebook & Instagram pages directly into your pipeline."
                connectedCount={pages.length}
                isConnected={step === 'connected' && pages.length > 0}
                onManage={() => setView('facebook')}
                onConnect={() => { setView('facebook'); }}
              />
            )}

            {/* Registry-driven cards — WhatsApp/Gupshup, Email, Gmail, Outlook, Twilio, Google Ads, AI Calling.
                Adding a new credential-based integration only requires a new entry in
                utils/integrationsRegistry.js — no JSX duplication needed here. */}
            {Object.entries(CRED_CONFIG).map(([key, cfg]) => {
              const status = getCredStatus(savedIntg?.[key]);
              if (statusFilter !== 'all' && statusFilter !== status) return null;
              return (
                <OverviewCard
                  key={key}
                  logo={<span style={{ fontSize: 24 }}>{cfg.icon}</span>}
                  name={cfg.title}
                  provider={cfg.provider}
                  description={cfg.overviewDescription || cfg.description}
                  isConnected={status === 'connected'}
                  onConnect={() => openCred(key)}
                  onManage={() => openCred(key)}
                />
              );
            })}

            {/* Zapier — coming soon, always shown under All/Available */}
            {(statusFilter === 'all' || statusFilter === 'available') && (
              <OverviewCard
                logo={<span style={{ fontSize: 24 }}>⚡</span>}
                name="Zapier"
                provider="Zapier, Inc."
                description="Connect 5000+ apps — auto-create leads from forms, sheets, email, and more."
                comingSoon
              />
            )}

            {/* Google Sheets — bespoke import/export flow */}
            {(statusFilter === 'all' || statusFilter === getCredStatus(savedIntg?.gsheets)) && (
              <OverviewCard
                logo={<span style={{ fontSize: 24 }}>📊</span>}
                name="Google Sheets"
                provider="Google"
                description="Import leads from a published Google Sheet URL, or export all leads as a spreadsheet."
                isConnected={!!savedIntg?.gsheets?.connected}
                onConnect={() => { setCredView('gsheets'); setCredForm({ sheetUrl: savedIntg?.gsheets?.sheetUrl || '' }); setView('gsheets'); }}
                onManage={() => { setCredView('gsheets'); setCredForm({ sheetUrl: savedIntg?.gsheets?.sheetUrl || '' }); setView('gsheets'); }}
              />
            )}
          </div>

          {/* Webhook */}
          <div style={{ marginTop: 32, padding: '18px 20px', background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>🔌 Custom Webhook</div>
            <p style={{ fontSize: 12, color: '#475569', marginBottom: 12 }}>Push leads from any source — website forms, JustDial, etc. POST a JSON body with <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 4 }}>name</code>, <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 4 }}>phone</code>, <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 4 }}>email</code>, <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 4 }}>source</code>.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <code style={{ flex: 1, background: '#f8fafc', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 8, padding: '9px 12px', fontSize: 12, color: '#7c3aed', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>https://us-central1-clarion-b9328.cloudfunctions.net/ingestLead</code>
              <button className="btn-ghost" onClick={() => navigator.clipboard.writeText('https://us-central1-clarion-b9328.cloudfunctions.net/ingestLead')} style={{ fontSize: 12, padding: '8px 12px', flexShrink: 0 }}>📋 Copy</button>
            </div>
          </div>
        </div>
      )}

      {/* ── FACEBOOK / META ADS ── */}
      {view === 'creds' && credView && CRED_CONFIG[credView] && (
        <div>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, fontSize: 13, color: '#475569' }}>
            <span onClick={() => { setView('overview'); setCredView(null); }} style={{ cursor: 'pointer', color: '#475569' }}>Integrations</span>
            <span style={{ color: '#d1d5db' }}>›</span>
            <span style={{ fontWeight: 600, color: '#0f172a' }}>{CRED_CONFIG[credView].title}</span>
          </div>

          {/* Platform-level integrations (Google, Microsoft, Zoom) — credentials
              are managed exclusively in the Super Admin Portal, not here. */}
          {CRED_CONFIG[credView].platformLevel ? (
            <div>
              <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 28, maxWidth: 680 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: CRED_CONFIG[credView].color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{CRED_CONFIG[credView].icon}</div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{CRED_CONFIG[credView].title}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{CRED_CONFIG[credView].provider}</div>
                  </div>
                  {savedIntg?.[credView]?.connected && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '3px 10px', borderRadius: 20, border: '1px solid rgba(16,185,129,0.2)' }}>
                      ● Connected
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 20 }}>{CRED_CONFIG[credView].description}</p>
                <div style={{ background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.18)', borderRadius: 12, padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>🔒</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#4c1d95', marginBottom: 4 }}>Configured at platform level</div>
                    <div style={{ fontSize: 12, color: '#6d28d9', lineHeight: 1.6 }}>
                      OAuth credentials for {CRED_CONFIG[credView].provider} are registered once for the entire platform — not per company.
                      To set up or update them, go to the <strong>Super Admin Portal → Platform Integrations</strong>.
                    </div>
                  </div>
                </div>
                <button className="btn-ghost" onClick={() => { setView('overview'); setCredView(null); }} style={{ marginTop: 20, fontSize: 12 }}>
                  ← Back to Integrations
                </button>
              </div>
            </div>
          ) : (
            <CredentialIntegrationDetail
              cfg={CRED_CONFIG[credView]}
              credForm={credForm}
              setCredForm={setCredForm}
              credSaving={credSaving}
              credSaved={credSaved}
              isConnected={!!savedIntg?.[credView]?.connected}
              onSave={() => saveCreds(credView)}
              onDisconnect={() => disconnectCred(credView)}
              onCancel={() => { setView('overview'); setCredView(null); }}
              extra={credView === 'email' ? <EmailDnsVerify domain={credForm.fromDomain} /> : null}
              personalMode={CRED_CONFIG[credView]?.supportsPersonalMode ? {
                value: credForm.accountMode === 'personal' ? 'personal' : 'work',
                available: !!oauthModes?.[CRED_CONFIG[credView].oauthProvider]?.personalAvailable,
                loading: oauthModesLoading,
                onChange: (mode) => setCredForm(p => ({ ...p, accountMode: mode })),
              } : null}
            />
          )}
        </div>
      )}

      {view === 'gsheets' && (
        <div>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, fontSize: 13, color: '#475569' }}>
            <span onClick={() => { setView('overview'); setGsheetsPreview(null); setGsheetsResult(null); setGsheetsError(''); }} style={{ cursor: 'pointer' }}>Integrations</span>
            <span style={{ color: '#d1d5db' }}>›</span>
            <span style={{ fontWeight: 600, color: '#0f172a' }}>Google Sheets</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            {/* Import card */}
            <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 22 }}>📥</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Import from Google Sheets</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Paste a public sheet URL to import leads</div>
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Google Sheet URL</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="crm-input"
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    value={gsheetsUrl}
                    onChange={e => setGsheetsUrl(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button className="btn-primary" onClick={() => fetchGsheet(gsheetsUrl)} disabled={!gsheetsUrl.trim() || gsheetsLoading} style={{ flexShrink: 0, padding: '0 14px' }}>
                    {gsheetsLoading ? '⏳' : '🔍 Load'}
                  </button>
                </div>
                {gsheetsError && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 6 }}>⚠ {gsheetsError}</div>}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  💡 Share your sheet: File → Share → Share with anyone (Viewer) → Copy link
                </div>
              </div>

              {/* Preview & mapping */}
              {gsheetsPreview && !gsheetsResult && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 10 }}>
                    ✅ Loaded {gsheetsPreview.rows.length} rows — Map columns to CRM fields:
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 14 }}>
                    {gsheetsPreview.headers.map((h, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: '#475569', width: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }} title={h}>{h}</span>
                        <span style={{ color: '#d1d5db', fontSize: 12 }}>→</span>
                        <select
                          className="crm-input"
                          value={gsheetsMapping[i] || ''}
                          onChange={e => setGsheetsMapping(p => ({ ...p, [i]: e.target.value }))}
                          style={{ flex: 1, height: 30, fontSize: 12 }}
                        >
                          <option value="">— Skip —</option>
                          {CRM_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
                    Preview: <b>{gsheetsPreview.rows[0]?.slice(0,3).join(', ')}</b>{gsheetsPreview.rows.length > 1 ? ` … and ${gsheetsPreview.rows.length - 1} more rows` : ''}
                  </div>
                  <button className="btn-primary" onClick={importGsheetsLeads} disabled={gsheetsImporting}
                    style={{ width: '100%', justifyContent: 'center' }}>
                    {gsheetsImporting ? '⏳ Importing…' : `📥 Import ${gsheetsPreview.rows.length} Leads`}
                  </button>
                </div>
              )}

              {gsheetsResult && (
                <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>✅</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>{gsheetsResult.imported} leads imported!</div>
                  <button className="btn-ghost" style={{ marginTop: 10, fontSize: 12 }} onClick={() => { setGsheetsResult(null); setGsheetsPreview(null); setGsheetsUrl(''); }}>Import another sheet</button>
                </div>
              )}
            </div>

            {/* Export card */}
            <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 22 }}>📤</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Export to Google Sheets</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Download CSV and open in Google Sheets</div>
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 20 }}>
                Download all <b>{leads.length} leads</b> as a CSV file, then import it into Google Sheets in one click.
              </div>
              <button className="btn-primary" onClick={exportLeadsCSV} style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}>
                ⬇️ Download CSV ({leads.length} leads)
              </button>
              <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', marginBottom: 8 }}>📋 How to open in Google Sheets</div>
                {[
                  'Click "Download CSV" above',
                  'Open sheets.new in your browser',
                  'File → Import → Upload → select the downloaded file',
                  'Choose "Replace spreadsheet" and click Import',
                ].map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 5 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#3b82f6', width: 16, flexShrink: 0 }}>{i + 1}.</span>
                    <span style={{ fontSize: 11, color: '#475569' }}>{step}</span>
                  </div>
                ))}
              </div>

              {savedIntg?.gsheets?.lastImport && (
                <div style={{ marginTop: 14, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                  Last import: {new Date(savedIntg.gsheets.lastImport).toLocaleString('en-IN')} · {savedIntg.gsheets.lastCount} leads
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {view === 'facebook' && (
        <div>
          {/* Breadcrumb */}
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
            <span onClick={() => setView('overview')} style={{ cursor: 'pointer', color: '#475569' }}>Integrations</span>
            <span style={{ margin: '0 6px' }}>›</span>
            <span>Meta Ads</span>
          </div>

          {/* Loading */}
          {step === 'loading' && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <span className="animate-spin" style={{ display: 'inline-block', width: 28, height: 28, borderRadius: '50%', border: '3px solid #1877f2', borderTopColor: 'transparent' }} />
            </div>
          )}

          {/* Connect screen — single click, no App ID needed */}
          {step === 'connect' && (
            <>
              <ConnectScreen
                onConnect={handleConnect}
                onBack={() => setView('overview')}
                loading={loadingConn}
                error={error}
              />
              {showDebug && debugInfo && (
                <div style={{ maxWidth: 440, margin: '0 auto 16px', padding: '14px', background: '#0f172a', borderRadius: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8' }}>Granted Permissions</span>
                    <button onClick={() => setShowDebug(false)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}>✕</button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {debugInfo.grantedPermissions.map(p => (
                      <span key={p} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 600, background: ['pages_show_list','leads_retrieval','pages_read_engagement'].includes(p) ? 'rgba(16,185,129,0.2)' : 'rgba(100,116,139,0.2)', color: ['pages_show_list','leads_retrieval','pages_read_engagement'].includes(p) ? '#34d399' : '#94a3b8' }}>{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Connected — Zilter-style account list */}
          {step === 'connected' && (
            <div style={{ display: 'flex', gap: 24 }}>
              {/* Main content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Page header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 18, borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                  <div style={{ width: 52, height: 52, borderRadius: 12, background: 'rgba(24,119,242,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MetaLogo size={30} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Meta Ads</h1>
                    <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Meta Platforms, Inc.</p>
                  </div>
                  <span style={{ padding: '5px 14px', borderRadius: 20, background: 'rgba(16,185,129,0.1)', color: '#10b981', fontSize: 12, fontWeight: 700, border: '1px solid rgba(16,185,129,0.25)' }}>
                    ● Connected
                  </span>
                </div>

                {/* Tabs row — Overview / Connections / Resources / Activity */}
                {(() => {
                  const totalForms = pages.reduce((sum, p) => sum + ((pageFormsMap[p.id]?.forms || []).length), 0);
                  const activityCount = pages.reduce((sum, p) => {
                    const forms = pageFormsMap[p.id]?.forms || [];
                    return sum + forms.filter(f => !!savedIntg?.facebook?.[`sync_${f.id}`]).length;
                  }, 0);
                  return (
                    <IntegrationDetailShell
                      tabs={[
                        { id: 'overview',    label: 'Overview' },
                        { id: 'connections', label: 'Connections', count: pages.length },
                        { id: 'resources',   label: 'Resources',   count: totalForms },
                        { id: 'activity',    label: 'Activity',    count: activityCount },
                      ]}
                      activeTab={fbTab}
                      onTabChange={setFbTab}
                      headerRight={
                        <button onClick={handleConnect} disabled={loadingConn}
                          style={{ padding: '7px 16px', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: loadingConn ? 'default' : 'pointer', background: 'linear-gradient(135deg,#1877f2,#3b82f6)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 6, opacity: loadingConn ? 0.7 : 1 }}>
                          {loadingConn ? '⏳ Fetching pages…' : '🔄 Reconnect & Refresh Pages'}
                        </button>
                      }
                    />
                  );
                })()}

                {/* ── OVERVIEW TAB ── */}
                {fbTab === 'overview' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '20px 22px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>About this integration</div>
                      <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.65, margin: 0 }}>
                        Imports Lead Ads submissions from your connected Facebook &amp; Instagram pages straight into your pipeline, with field mapping you approve once per form and automatic re-sync every 15 minutes.
                      </p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '16px 18px' }}>
                        <DetailRow label="Pages Connected" value={pages.length} />
                      </div>
                      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '16px 18px' }}>
                        <DetailRow label="Lead Forms Found" value={pages.reduce((sum, p) => sum + ((pageFormsMap[p.id]?.forms || []).length), 0)} />
                      </div>
                      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '16px 18px' }}>
                        <DetailRow label="Leads Imported" value={fbLeadsInCRM.length} />
                      </div>
                      <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '16px 18px' }}>
                        <DetailRow label="Connected Since" value={savedIntg?.facebook?.connectedAt ? new Date(savedIntg.facebook.connectedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'} />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── CONNECTIONS TAB ── */}
                {fbTab === 'connections' && (
                <>
                {/* No pages — explain why and offer fix */}
                {pages.length === 0 && !loadingConn && (
                  <div style={{ marginBottom: 16, padding: '16px 18px', borderRadius: 12, background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.25)' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309', marginBottom: 8 }}>⚠️ No pages found — here's how to fix it</div>
                    <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.8 }}>
                      <strong>Most common reason:</strong> Your Facebook Page is managed through <strong>Meta Business Suite</strong> (Business Manager), not directly from your personal profile.
                      <br/>Try these steps:
                    </div>
                    <ol style={{ margin: '8px 0 0 16px', padding: 0, fontSize: 12, color: '#78350f', lineHeight: 2 }}>
                      <li>Click <strong>Reconnect &amp; Refresh Pages</strong> above and approve <em>all</em> permissions when Facebook asks</li>
                      <li>Make sure you are an <strong>Admin</strong> (not just Editor) on the Facebook Page</li>
                      <li>If your page is in Business Manager — go to <strong>Meta Business Suite → Settings → Pages</strong> and ensure your personal account has Admin access</li>
                      <li>Still not showing? Use <strong>"Add a page by ID"</strong> below — find your Page ID from facebook.com/your-page → About → Page Transparency</li>
                    </ol>
                  </div>
                )}

                {/* Token-expired soft banner — show pages but prompt reconnect */}
                {tokenExpired && (
                  <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 18 }}>⏱️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>Facebook token expired</div>
                      <div style={{ fontSize: 12, color: '#78350f', marginTop: 2 }}>Your access token has expired. Your pages are still visible below — click <strong>Reconnect &amp; Refresh Pages</strong> to get a fresh 60-day token.</div>
                    </div>
                    <button onClick={handleConnect} disabled={loadingConn}
                      style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg,#f59e0b,#ef4444)', color: '#fff', border: 'none' }}>
                      Reconnect now
                    </button>
                  </div>
                )}

                {/* Error banner — shown regardless of tokenExpired so sync errors are always visible */}
                {error && pages.length > 0 && (
                  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>{error}</div>
                )}

                {/* Account (page) list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {pages.map(page => {
                    const pf = pageFormsMap[page.id] || { forms: [], loading: false, error: null };
                    return (
                      <PageAccountCard
                        key={page.id}
                        page={page}
                        pf={pf}
                        syncing={syncing}
                        syncResults={syncResults}
                        savedIntg={savedIntg}
                        getImportedCount={getImportedCount}
                        onSync={handleSyncForm}
                        onDisconnect={() => handleDisconnect(page.id)}
                        onRetryPAT={(pat) => handleRetryWithPAT(page, pat)}
                        onReload={() => loadPageForms(page)}
                      />
                    );
                  })}
                </div>

                {/* Add account */}
                <div style={{ marginTop: 16 }}>
                  {!showAddPage ? (
                    <button onClick={() => setShowAddPage(true)} style={{ padding: '9px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'rgba(24,119,242,0.07)', border: '1px solid rgba(24,119,242,0.2)', color: '#1877f2', display: 'flex', alignItems: 'center', gap: 7 }}>
                      + Add Account
                    </button>
                  ) : (
                    <div style={{ background: '#f8fafc', border: '1px solid rgba(0,0,0,0.07)', borderRadius: 12, padding: '16px 18px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>Add a page by ID</div>
                      <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>Find your Page ID: facebook.com/your-page → About → Page Transparency → Page ID</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input className="crm-input" placeholder="e.g. 123456789012345" value={manualPageId} onChange={e => setManualPageId(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleManualPage()} style={{ flex: 1, height: 40, fontSize: 13 }} />
                        <button onClick={handleManualPage} disabled={addingManual || !manualPageId.trim()} style={{ height: 40, padding: '0 20px', borderRadius: 9, background: 'linear-gradient(135deg,#1877f2,#3b82f6)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                          {addingManual ? '…' : 'Add'}
                        </button>
                        <button onClick={() => { setShowAddPage(false); setManualPageId(''); }} style={{ height: 40, padding: '0 14px', borderRadius: 9, background: 'transparent', border: '1px solid rgba(0,0,0,0.1)', color: '#64748b', fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </>
                )}

                {/* ── RESOURCES TAB — real discovered Pages + their Lead Forms ── */}
                {fbTab === 'resources' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {pages.length === 0 ? (
                      <EmptyState icon="📄" title="No pages connected yet" subtitle="Connect a Facebook Page in the Connections tab to see its discovered Lead Forms here." />
                    ) : pages.map(page => {
                      const pf = pageFormsMap[page.id] || { forms: [], loading: false, error: null };
                      return (
                        <div key={page.id} style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '16px 18px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                            <span style={{ fontSize: 16 }}>📘</span>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{page.name}</div>
                            <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>{pf.forms.length} lead form{pf.forms.length !== 1 ? 's' : ''}</span>
                          </div>
                          {pf.loading ? (
                            <div style={{ fontSize: 12, color: '#94a3b8' }}>Loading forms…</div>
                          ) : pf.forms.length === 0 ? (
                            <div style={{ fontSize: 12, color: '#94a3b8' }}>No Lead Ads forms found on this page yet.</div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {pf.forms.map(form => {
                                const mapped = !!savedIntg?.facebook?.pages?.[page.id]?.forms?.[form.id]?.mapping;
                                return (
                                  <div key={form.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#f8fafc', borderRadius: 8 }}>
                                    <span style={{ fontSize: 13, color: '#0f172a', flex: 1 }}>{form.name}</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: mapped ? '#10b981' : '#94a3b8' }}>{mapped ? 'Mapped' : 'Not mapped yet'}</span>
                                    <span style={{ fontSize: 11, color: '#64748b' }}>{getImportedCount(form.id)} imported</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── ACTIVITY TAB — real log derived from sync timestamps ── */}
                {fbTab === 'activity' && (() => {
                  const events = [];
                  pages.forEach(page => {
                    const forms = pageFormsMap[page.id]?.forms || [];
                    forms.forEach(form => {
                      const lastSync = savedIntg?.facebook?.[`sync_${form.id}`];
                      if (lastSync) {
                        events.push({
                          key: form.id,
                          pageName: page.name,
                          formName: form.name,
                          imported: getImportedCount(form.id),
                          time: new Date(lastSync),
                        });
                      }
                    });
                  });
                  events.sort((a, b) => b.time - a.time);
                  return events.length === 0 ? (
                    <EmptyState icon="🕒" title="No sync activity yet" subtitle="Once a lead form is synced, each sync event will show up here with a timestamp and import count." />
                  ) : (
                    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '4px 18px' }}>
                      {events.map(ev => (
                        <ActivityRow
                          key={ev.key}
                          icon="🔄"
                          title={`Synced "${ev.formName}"`}
                          subtitle={`${ev.pageName} · ${ev.imported} lead${ev.imported !== 1 ? 's' : ''} imported`}
                          time={ev.time.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        />
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Sidebar — mirrors Zilter's Details panel */}
              <div style={{ width: 210, flexShrink: 0 }}>
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Details</div>
                  </div>
                  <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Version</div>
                      <div style={{ fontSize: 13, color: '#0f172a' }}>v19.0</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</div>
                      <div style={{ fontSize: 13, color: '#0f172a' }}>Lead Generation</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pages Connected</div>
                      <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 700 }}>{pages.length}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Leads Imported</div>
                      <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 700 }}>{fbLeadsInCRM.length}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Connected Since</div>
                      <div style={{ fontSize: 13, color: '#0f172a' }}>
                        {savedIntg?.facebook?.connectedAt
                          ? new Date(savedIntg.facebook.connectedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>App ID</div>
                      <div style={{ fontSize: 11, color: '#475569', fontFamily: 'monospace', wordBreak: 'break-all' }}>{platformAppId}</div>
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <a href="https://developers.facebook.com/docs/marketing-api/guides/lead-ads/" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1877f2', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                      📄 View Documentation
                    </a>
                    <button onClick={() => handleDisconnect()} style={{ width: '100%', padding: '8px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444' }}>
                      Disconnect All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── FB Field Mapping Dialog ── */}
      {mappingDialog && (
        <FBMappingDialog
          dialog={mappingDialog}
          importing={mappingImporting}
          allSources={mergedSources}
          onCancel={() => setMappingDialog(null)}
          onConfirm={(finalMapping, source) => handleConfirmMapping(finalMapping, source)}
        />
      )}

    </div>
  );
}

// ─── Overview card ────────────────────────────────────────────────────────────
function OverviewCard({ logo, name, provider, description, connectedCount, isConnected, onManage, onConnect, comingSoon }) {
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(24,119,242,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {logo}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{name}</span>
            {isConnected && connectedCount > 0 && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)' }}>
                {connectedCount} Connected
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{provider}</div>
        </div>
      </div>
      <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.6, flex: 1, margin: 0 }}>{description}</p>
      {!comingSoon ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {isConnected ? (
            <button onClick={onManage} style={{ flex: 1, padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: '#0f172a', color: '#fff', border: 'none' }}>Manage</button>
          ) : (
            <button onClick={onConnect} style={{ flex: 1, padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg,#1877f2,#3b82f6)', color: '#fff', border: 'none' }}>Connect</button>
          )}
          <button style={{ padding: '9px 14px', borderRadius: 8, fontSize: 11, cursor: 'default', background: 'transparent', border: '1px solid rgba(0,0,0,0.1)', color: '#475569' }}>Learn More</button>
        </div>
      ) : (
        <span style={{ fontSize: 11, color: '#94a3b8', background: 'rgba(0,0,0,0.04)', padding: '4px 10px', borderRadius: 20, display: 'inline-block', marginTop: 4, width: 'fit-content' }}>Coming soon</span>
      )}
    </div>
  );
}

// ─── Page account card (Zilter-style) ────────────────────────────────────────
function PageAccountCard({ page, pf, syncing, syncResults, savedIntg, getImportedCount, onSync, onDisconnect, onRetryPAT, onReload }) {
  const [patInput,    setPatInput]    = React.useState('');
  const [showPAT,     setShowPAT]     = React.useState(false);
  const isSyncingPage = syncing?.pageId === page.id;

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
      {/* Account header — matches Zilter's bold account row */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{page.name}</div>
        <button onClick={onDisconnect} style={{ padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'rgba(239,68,68,0.09)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', display: 'flex', alignItems: 'center', gap: 5 }}>
          ⏏ Disconnect
        </button>
      </div>

      {/* Forms */}
      {pf.loading ? (
        <div style={{ padding: '18px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="animate-spin" style={{ display: 'inline-block', width: 16, height: 16, borderRadius: '50%', border: '2px solid #1877f2', borderTopColor: 'transparent', flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: '#64748b' }}>Loading lead gen forms…</span>
        </div>
      ) : pf.error === 'token_expired' ? (
        <div style={{ padding: '14px 16px', fontSize: 12, color: '#92400e', background: 'rgba(245,158,11,0.06)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          ⏱️ Token expired — use <strong>Reconnect &amp; Refresh Pages</strong> above to restore form access.
        </div>
      ) : pf.error === 'missing_permission' ? (
        <div style={{ padding: '14px 16px', fontSize: 12, color: '#7c3aed', background: 'rgba(124,58,237,0.05)', borderRadius: 8 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>🔐 Missing permission: <code>pages_manage_ads</code></div>
          <div style={{ color: '#4c1d95', lineHeight: 1.6 }}>
            Click <strong>Reconnect &amp; Refresh Pages</strong> and when Facebook asks, make sure to approve <strong>all permissions</strong> including "Manage ads for Pages you manage". If the permission dialog doesn't appear, click <strong>Edit</strong> on the permissions screen to re-request them.
          </div>
        </div>
      ) : pf.error ? (
        <div style={{ padding: '14px 16px', fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
          ⚠ {pf.error}
          <button onClick={onReload} style={{ fontSize: 11, color: '#1877f2', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
        </div>
      ) : pf.forms.length === 0 ? (
        <div style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
            No lead gen forms found.{' '}
            <a href="https://adsmanager.facebook.com" target="_blank" rel="noreferrer" style={{ color: '#1877f2', fontWeight: 600 }}>Create a Lead Ad →</a>
          </div>
          {!showPAT ? (
            <button onClick={() => setShowPAT(true)} style={{ fontSize: 12, color: '#475569', background: 'none', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 7, padding: '5px 12px', cursor: 'pointer' }}>
              Have forms? Paste page token to retry
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="crm-input" placeholder="Paste Page Access Token" value={patInput} onChange={e => setPatInput(e.target.value)} style={{ height: 36, fontSize: 12, flex: 1 }} />
              <button onClick={() => { onRetryPAT(patInput); setShowPAT(false); }} disabled={!patInput.trim()} style={{ height: 36, padding: '0 14px', borderRadius: 7, background: '#1877f2', color: '#fff', border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>Retry</button>
            </div>
          )}
        </div>
      ) : (
        <>
          {pf.forms.map((form, idx) => {
            const isSyncing = isSyncingPage && syncing?.formId === form.id;
            const res        = syncResults[form.id];
            const lastSync   = savedIntg?.facebook?.[`sync_${form.id}`];
            const imported   = getImportedCount(form.id);
            return (
              <div key={form.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: idx < pf.forms.length - 1 ? '1px solid #f1f5f9' : 'none', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {lastSync
                      ? new Date(lastSync).toLocaleString('en-IN', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : form.created_time
                      ? new Date(form.created_time).toLocaleString('en-IN', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : 'Not synced yet'
                    }
                    {imported > 0 && <span style={{ color: '#10b981', fontWeight: 600 }}>· {imported} in CRM</span>}
                    {form.leads_count > 0 && <span style={{ color: '#7c3aed', fontWeight: 600 }}>· {form.leads_count} on FB</span>}
         
                    {res && <span style={{ color: res.error ? '#dc2626' : res.imported > 0 ? '#10b981' : '#64748b', fontWeight: 600 }}>· {res.error ? '⚠ Token expired — reconnect' : res.imported > 0 ? `+${res.imported} imported` : `Up to date (${res.total} on FB)`}</span>}
                  </div>
                </div>
                <button onClick={() => onSync(form, page)} disabled={isSyncing} style={{ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: isSyncing ? 'wait' : 'pointer', background: isSyncing ? '#f1f5f9' : '#eff6ff', border: '1px solid #bfdbfe', color: '#1877f2', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                  {isSyncing
                    ? <><span className="animate-spin" style={{ display: 'inline-block', width: 11, height: 11, borderRadius: '50%', border: '2px solid #1877f2', borderTopColor: 'transparent' }} /> Syncing…</>
                    : '↓ Sync'}
                </button>
              </div>
            );
          })}
        </>
      )}

      {/* + New Setup */}
      <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9' }}>
        <a href="https://adsmanager.facebook.com" target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#1877f2', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}>
          + New Setup
        </a>
      </div>
    </div>
  );
}
